import React from 'react';
import {
    DrawerList,
    DrawerSectionsWrapper,
    ResponsiveDrawer,
} from '../../buildDistribution/mobileComponents/ResponsiveDrawer';
import Text from 'antd/lib/typography/Text';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import Space from 'antd/lib/space';
import { ProfileOutlined } from '@ant-design/icons';
import { AppPlatform, Device, ProvisioningProfile } from '../../../types';
import { flatten } from 'lodash';
import {
    displayNameForAppleCertificateType,
    displayTextForProvisioningProfileType,
} from '../../../helpers/displayHelpers';
import { DeviceDetailsDrawer, ShortUDIDComponent } from './DeviceDetailsDrawer';
import { FetchablePaginatedEntityType } from '../../../entityType';
import { syncableProvisioningProfileTypesForPlatform } from '../../../helpers';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { selectAppDevices } from '../../../selectors/selectors';
import { AppComponentProps } from '../../../typescriptHelpers';
import { globalMessage } from '../../../utils/globalMessage';
import { ThunderboltFilled } from '@ant-design/icons';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '../../../timeHelpers';
import { useGetEntity } from '@/utils/hooks/helpers';

export interface ProfileDetailsDrawerProps extends AppComponentProps {
    profile?: ProvisioningProfile;
    platform: AppPlatform;
    onClose: () => void;
    afterOpenChange?: (boolean) => void;
}

export const ProfileDetailsDrawer: React.FC<ProfileDetailsDrawerProps> = (props) => {
    const { onClose, profile, afterOpenChange, appId, orgId } = props;

    const title = profile?.name;

    const [highlightedDevice, setHighlightedDevice] = React.useState<Device | null>(null);

    const [getAllDevices, getAllDevicesIsLoading, getAllDevicesError] = useGetEntity(
        FetchablePaginatedEntityType.AllDevices,
        appId,
    );

    const devices = useSelector((state: RootState) => selectAppDevices(state, appId));

    const relatedDevices = React.useMemo(() => {
        return devices?.filter((device) =>
            profile?.devices?.some((profileDevice) => profileDevice.udid === device.udid),
        );
    }, [devices, profile]);

    React.useEffect(() => {
        if (!devices) {
            getAllDevices({
                appId,
                orgId,
            });
        }
    }, [devices]);

    React.useEffect(() => {
        if (getAllDevicesError) {
            globalMessage.error(getAllDevicesError);
        }
    }, [getAllDevicesError]);

    const showDevicesSection =
        profile ? syncableProvisioningProfileTypesForPlatform(props.platform).includes(profile.profileType) : true;

    return (
        <ResponsiveDrawer
            open={!!profile}
            onClose={onClose}
            afterOpenChange={afterOpenChange}
            header={
                <Space size={20}>
                    <ProfileOutlined
                        style={{
                            fontSize: 24,
                            color: runwayColors.primary,
                        }}
                    />
                    <Text
                        style={{
                            fontSize: 16,
                            color: antGrey.grey8,
                        }}
                    >
                        {title}
                    </Text>
                </Space>
            }
            content={
                <DrawerSectionsWrapper>
                    <Space
                        direction="vertical"
                        size={22}
                        style={{
                            width: '100%',
                            padding: 16,
                            marginTop: 16,
                        }}
                    >
                        <DrawerList
                            items={[
                                {
                                    title: 'Bundle ID',
                                    description: profile?.bundleId,
                                },
                                {
                                    title: 'Profile type',
                                    description: displayTextForProvisioningProfileType(profile?.profileType),
                                },
                                ...flatten(
                                    profile?.certificates?.map((certificate) => [
                                        {
                                            title: 'Certificate name',
                                            description: certificate.name,
                                        },
                                        {
                                            title: 'Certificate type',
                                            description: displayNameForAppleCertificateType(
                                                certificate.certificateType,
                                            ),
                                        },
                                    ]) ?? [],
                                ),
                                {
                                    title: 'Created on',
                                    description: (
                                        <Space direction="vertical" align="end">
                                            <Text>
                                                {dayjs(profile?.creationDate).format(DatetimeFormat.ShortMonthDateYear)}
                                            </Text>
                                            {profile?.createdByRunwayAt && (
                                                <Space>
                                                    <ThunderboltFilled style={{ color: runwayColors.primary }} />
                                                    <Text type="secondary">This profile was created by Runway.</Text>
                                                </Space>
                                            )}
                                        </Space>
                                    ),
                                },
                            ]}
                        />
                        {showDevicesSection && (
                            <Space
                                direction="vertical"
                                style={{
                                    width: '100%',
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 12,
                                        color: antGrey.grey7,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                    }}
                                >
                                    Associated devices
                                </Text>
                                <DeviceDetailsDrawer
                                    appId={appId}
                                    orgId={orgId}
                                    device={highlightedDevice}
                                    onClose={() => setHighlightedDevice(null)}
                                    shouldShowRegistrationStatus
                                />

                                <DrawerList
                                    isLoading={getAllDevicesIsLoading}
                                    items={relatedDevices?.map((device) => ({
                                        onClick: () => setHighlightedDevice(device),
                                        title: (
                                            <Space>
                                                <Text
                                                    ellipsis
                                                    style={{
                                                        color: antGrey.grey8,
                                                        fontSize: 14,
                                                    }}
                                                >
                                                    {device.displayName}
                                                </Text>
                                                <ShortUDIDComponent udid={device.udid} maxWidth={100} />
                                            </Space>
                                        ),
                                        description: device.associatedUserEmail ?? (
                                            <Text type="secondary">No associated user</Text>
                                        ),
                                    }))}
                                />
                            </Space>
                        )}
                    </Space>
                </DrawerSectionsWrapper>
            }
        />
    );
};
